import pickBy from 'lodash-es/pickBy';
import { createSelector } from '@reduxjs/toolkit';
import { USER_CONSTANTS } from '../constants';
import { CLUSTER_CONSTANTS, CLUSTER_TYPE } from '../constants/clusterConstants';
import { PERMISSIONS_KEYS_BY_ACTION } from '../constants/permissionsConstants';
import { DRIVER_ROLES } from '../constants/userConstants';
import { ClusterId, selectClusterIds, selectClustersById } from '../slices/clusters';
import { ColorItem } from '../slices/colors';
import { isPluginActivated } from '../utils/clusters';
import { getColorWithHash } from '../utils/colors';
import { hasPermission } from '../utils/permissions';

export const selectClusterArray = createSelector([selectClustersById, selectClusterIds], (clusters, clusterIds) =>
  clusterIds.map((id) => clusters[id]).filter(Boolean),
);

const selectAdminClusters = createSelector([selectClusterArray], (clusters) =>
  clusters.filter((c) => c.role === USER_CONSTANTS.ROLE.ADMIN),
);

export const selectClustersWithRole = createSelector([selectClusterArray], (clusters) =>
  clusters.filter((c) => c.role && Object.values(USER_CONSTANTS.ROLE).includes(c.role)),
);

export const selectParentClusters = createSelector([selectClusterArray], (clusters) => {
  // If there is only one cluster and it's a farm, we must consider it as a cluster parent
  if (clusters.length === 1 && clusters[0]!.cluster_type === CLUSTER_TYPE.FARM) {
    return clusters;
  }
  const clusterTypes = Object.values(CLUSTER_TYPE).filter((t) => t !== CLUSTER_TYPE.FARM);
  return clusters.filter((cluster) => clusterTypes.includes(cluster.cluster_type));
});

export const selectAdminParentClustersWithoutLinkedPartner = createSelector([selectParentClusters], (clusters) =>
  clusters.filter((cluster) => cluster.role === USER_CONSTANTS.ROLE.ADMIN && !cluster.linked_partner),
);

export const selectNumberOfDemoClusters = createSelector(
  [selectClusterArray],
  (clusters) => clusters.filter((c) => c.is_demo).length,
);

export const selectIsViticultureContext = createSelector([selectAdminClusters], (clusters) =>
  clusters.some((c) => c?.activities?.length === 1 && c.activities[0] === CLUSTER_CONSTANTS.ACTIVITIES.viticulture),
);

export const selectIsDeneigementContext = createSelector([selectAdminClusters], (clusters) =>
  clusters.some((c) => c?.activities?.length === 1 && c.activities[0] === CLUSTER_CONSTANTS.ACTIVITIES.deneigement),
);

export const selectParentClustersWhichCanCreateClusterMember = createSelector([selectParentClusters], (clusters) => {
  return clusters.filter((c) => c.authorizations && c.authorizations.can_create_cluster_member);
});

export const selectClustersWhichCanCreateDriver = createSelector([selectClustersWithRole], (clusters) =>
  clusters.filter(
    (c) =>
      Array.isArray(c.authorizations?.can_create_user) &&
      c.authorizations.can_create_user.some((r) => DRIVER_ROLES.includes(r)),
  ),
);

export const selectClustersByIdWhichCanCreateParcel = createSelector([selectClustersById], (clusters) => {
  return pickBy(clusters, (c) => c.authorizations[PERMISSIONS_KEYS_BY_ACTION.create_parcel]);
});

export const selectClustersWhichCanCreateTask = createSelector([selectParentClusters], (clusters) => {
  return clusters.filter(
    (cluster) =>
      (isPluginActivated(cluster, CLUSTER_CONSTANTS.PLUGINS_NAMES.phyto) ||
        isPluginActivated(cluster, CLUSTER_CONSTANTS.PLUGINS_NAMES.worksite_management)) &&
      cluster.authorizations[PERMISSIONS_KEYS_BY_ACTION.create_task],
  );
});

export const selectClustersWithPlanning = createSelector([selectParentClusters], (clusters) =>
  clusters.filter((cluster) => isPluginActivated(cluster, CLUSTER_CONSTANTS.PLUGINS_NAMES.planning)),
);

export const selectChildClusters = createSelector(
  [selectClusterArray, (_state: unknown, parentClusterId: ClusterId | null) => parentClusterId],
  (clusters, parentClusterId) =>
    parentClusterId ? clusters.filter((cluster) => cluster.parent_cluster_ids?.includes(parentClusterId)) : [],
);

export const selectClustersWithEquipmentBooking = createSelector([selectParentClusters], (clusters) =>
  clusters.filter(
    (cluster) =>
      isPluginActivated(cluster, CLUSTER_CONSTANTS.PLUGINS_NAMES.equipment_booking) &&
      hasPermission(cluster, PERMISSIONS_KEYS_BY_ACTION.read_equipment_booking),
  ),
);

export const selectClustersWithEquipmentMaintenance = createSelector([selectParentClusters], (clusters) =>
  clusters.filter((cluster) => isPluginActivated(cluster, CLUSTER_CONSTANTS.PLUGINS_NAMES.equipment_maintenance)),
);

export const selectClusterColorItems = createSelector([selectClusterArray], (clusters) => {
  return clusters
    .filter((cluster) => !!cluster.color)
    .map(
      (cluster) =>
        ({
          id: String(cluster.id),
          name: cluster.name,
          color: getColorWithHash(cluster.color!),
        }) satisfies ColorItem,
    );
});
